import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Clock } from 'lucide-react';
import SourceCombobox from '../common/SourceCombobox';

export interface PayoutFrequencyData {
  payoutSchedule: string;
  payoutDay: string;
  paymentMethod: string;
  autoWithdrawal: boolean;
}

interface PayoutFrequencyProps {
  data: PayoutFrequencyData;
  onChange: (data: PayoutFrequencyData) => void;
}

const payoutScheduleOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const payoutDayOptions = [
  { value: '1st', label: '1st of month' },
  { value: '15th', label: '15th of month' },
  { value: 'last-day', label: 'Last day of month' },
];

const paymentMethodOptions = [
  { value: 'bank-transfer', label: 'Bank Transfer' },
  { value: 'cryptocurrency', label: 'Cryptocurrency' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'wire-transfer', label: 'Wire Transfer' },
];

const getOptionLabel = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string
) => options.find(option => option.value === value)?.label || '';

const PayoutFrequency = ({ data, onChange }: PayoutFrequencyProps) => {
  const handleChange = (field: keyof PayoutFrequencyData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-500/10 rounded-lg">
            <Clock className="h-5 w-5 text-green-500" />
          </div>
          <div>
            <CardTitle>Payout Frequency</CardTitle>
            <CardDescription>
              Set payment schedule and payout preferences
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="payout-schedule">Payout Schedule</Label>
          <SourceCombobox
            id="payout-schedule"
            items={payoutScheduleOptions.map(option => option.label)}
            placeholder="Select payout schedule"
            value={getOptionLabel(payoutScheduleOptions, data.payoutSchedule)}
            onChange={value => {
              const option = payoutScheduleOptions.find(
                item => item.label === value
              );
              if (option) handleChange('payoutSchedule', option.value);
            }}
            emptyText="No payout schedules found."
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="payout-day">Payout Day</Label>
          <SourceCombobox
            id="payout-day"
            items={payoutDayOptions.map(option => option.label)}
            placeholder="Select payout day"
            value={getOptionLabel(payoutDayOptions, data.payoutDay)}
            onChange={value => {
              const option = payoutDayOptions.find(
                item => item.label === value
              );
              if (option) handleChange('payoutDay', option.value);
            }}
            emptyText="No payout days found."
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="payment-method">Payment Method</Label>
          <SourceCombobox
            id="payment-method"
            items={paymentMethodOptions.map(option => option.label)}
            placeholder="Select payment method"
            value={getOptionLabel(paymentMethodOptions, data.paymentMethod)}
            onChange={value => {
              const option = paymentMethodOptions.find(
                item => item.label === value
              );
              if (option) handleChange('paymentMethod', option.value);
            }}
            emptyText="No payment methods found."
          />
        </div>
        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            id="auto-withdraw"
            checked={data.autoWithdrawal}
            onChange={e => handleChange('autoWithdrawal', e.target.checked)}
          />
          <Label htmlFor="auto-withdraw" className="text-sm">
            Enable auto-withdrawal
          </Label>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayoutFrequency;
